import Image from '@plone/volto/components/theme/Image/Image';
import cx from 'classnames';
import PropTypes from 'prop-types';
import { defineMessages, useIntl } from 'react-intl';

import userIconSVG from './user-icon.svg';

const messages = defineMessages({
  noPhoto: {
    id: 'personImage.noPhoto',
    defaultMessage: 'Pessoa sem foto',
  },
});

export const PersonImage = ({ person, sm, md, lg }) => {
  const intl = useIntl();

  // Buscar halign e valign do objeto person
  const halign = person?.halign || 'center';
  const valign = person?.valign || 'center';

  // Mapear valores de alinhamento para uma única classe CSS combinada
  const getPositionClass = (h, v) => {
    return `object-position-${h}-${v}`;
  };

  const alignmentClass = getPositionClass(halign, valign);

  const hasImageScales = !!(
    person?.image_scales ||
    person?.image?.scales ||
    person?.image_scales?.image
  );
  // "Imagem simples" = objeto com download/url/href mas sem scales
  const hasSimpleImage = !!(
    person?.image &&
    (person.image.download || person.image.url || person.image.href) &&
    !person.image.scales &&
    !person.image_scales
  );
  // "Imagem base64" = objeto com data em base64
  const hasBase64Image = !!(
    person?.image &&
    person.image.data &&
    person.image['content-type'] &&
    person.image.encoding === 'base64'
  );
  // hasImage não é usado diretamente; decisão ramificada abaixo

  let sizeValue = '160px';
  if (sm) sizeValue = '100px';
  else if (md) sizeValue = '100px';
  else if (lg) sizeValue = '160px';
  const numericSize = parseInt(sizeValue);

  const classes = cx('br-avatar', {
    small: sm,
    medium: md,
    large: !md && !sm,
  });

  let content;
  if (hasImageScales) {
    // Mantém comportamento padrão com o componente Volto
    content = (
      <Image
        item={person}
        loading="lazy"
        alt={person.title || 'Foto da pessoa'}
        className={`object-fit-cover ${alignmentClass}`}
        imageField="image"
        sizes={sizeValue}
        responsive={true}
        height={numericSize}
        width={numericSize}
      />
    );
  } else if (hasBase64Image) {
    // Imagem em formato base64
    const base64Src = `data:${person.image['content-type']};base64,${person.image.data}`;
    content = (
      <Image
        src={base64Src}
        loading="lazy"
        alt={person.title || 'Foto da pessoa'}
        className={`object-fit-cover ${alignmentClass}`}
        imageField="image"
        sizes={sizeValue}
        responsive={true}
        height={numericSize}
        width={numericSize}
      />
    );
  } else if (hasSimpleImage) {
    const direct =
      person.image.download ||
      person.image.url ||
      person.image.href ||
      (person['@id']
        ? `${person['@id'].replace(/\/$/, '')}/@@images/image`
        : undefined);
    content = (
      <Image
        src={direct}
        loading="lazy"
        alt={person.title || 'Foto da pessoa'}
        className={`object-fit-cover ${alignmentClass}`}
        sizes={sizeValue}
        responsive={false}
        height={numericSize}
        width={numericSize}
      />
    );
  } else {
    content = (
      <Image
        src={userIconSVG}
        loading="lazy"
        alt={intl.formatMessage(messages.noPhoto)}
        className={`object-fit-cover ${alignmentClass}`}
        responsive={false}
        height={numericSize}
        width={numericSize}
      />
    );
  }

  return (
    <span className={classes}>
      <span className="content">{content}</span>
    </span>
  );
};

PersonImage.propTypes = {
  person: PropTypes.shape({
    halign: PropTypes.oneOf(['left', 'center', 'right']),
    valign: PropTypes.oneOf(['top', 'center', 'bottom']),
    title: PropTypes.string,
    image: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    image_scales: PropTypes.object,
  }),
  sm: PropTypes.bool,
  md: PropTypes.bool,
  lg: PropTypes.bool,
};

PersonImage.defaultProps = {
  person: {},
};
